import { usePhotoUploader, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { getUniqueScanPhotos, ScanPartOptimized, ScanPhoto, ScanRootOptimized } from '@kaiizen/kai-scan-template-system';

import { ANALYTICS_EVENTS, trackEvent } from '@/Client/analytics';
import { usePhotoUploadProgressReporter } from '@/Client/State/PhotoUploadProgressContext';

/**
 * Detects if a file is a JPEG image by checking the file's MIME type and magic bytes.
 */
export const isJpegFile = async (file: File): Promise<boolean> => {
  if (file.type === 'image/jpeg') return true;
  const buffer = await file.slice(0, 4).arrayBuffer();
  const bytes = new Uint8Array(buffer);
  return bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
};

/**
 * Converts a non-JPEG image file to JPEG format using Canvas.
 */
export const convertToJpeg = async (file: File, quality = 0.9): Promise<File> => {
  const objectUrl = URL.createObjectURL(file);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      canvas.width = img.width;
      canvas.height = img.height;
      context.drawImage(img, 0, 0);
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Failed to convert image to JPEG'));
            return;
          }
          resolve(
            new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: file.lastModified,
            }),
          );
        },
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };
    img.src = objectUrl;
  });
};

/**
 * Ensures a file is JPEG. Returns the original if already JPEG, otherwise converts via Canvas.
 */
export const ensureJpegFormat = async (file: File, quality = 0.9): Promise<File> => {
  const isJpeg = await isJpegFile(file);
  return isJpeg ? file : convertToJpeg(file, quality);
};

/**
 * Converts files to JPEG and uploads each via the provided uploader, with per-file error handling.
 * Reports progress after every file (success or failure alike) so a caller showing "N/total" always
 * reaches the total, even if some files failed along the way.
 */
const bulkUploadPhotos = async (
  files: File[],
  photoUploader: ReturnType<typeof usePhotoUploader>,
  onProgress: (completed: number) => void,
): Promise<ScanPhoto[]> => {
  const results: ScanPhoto[] = [];
  let completed = 0;
  for (const file of files) {
    try {
      const jpegFile = await ensureJpegFormat(file);
      await photoUploader(jpegFile, photo => results.push(photo));
    } catch (error) {
      alert(`Error uploading photo: ${file.name} - ${error instanceof Error ? error.message : String(error)}`);
    }
    onProgress(++completed);
  }
  return results;
};

export const useBulkPhotoUploader = () => {
  const photoUploader = usePhotoUploader();
  const { scanDataMutators, scanId } = useScanEditingContextSlice(['scanDataMutators', 'scanId']);
  const reportUploadProgress = usePhotoUploadProgressReporter();
  /** `scanPart` also accepts `ScanRootOptimized` — the "Curbside Photo" scope-level target
   *  (`scanPartIds: {}`) has no group or task, so its scan part is the scan root itself. */
  return async (
    filesOrFileList: File[] | FileList | null | undefined,
    scanPart: ScanPartOptimized | ScanRootOptimized,
    scanPartIds: { groupId?: string; taskId?: string; optionId?: string; lineItemId?: string },
  ) => {
    if (!filesOrFileList) return;
    const files = (filesOrFileList instanceof FileList ? Array.from(filesOrFileList) : filesOrFileList).filter(file => file.type.startsWith('image/'));
    if (files.length === 0) return;
    const total = files.length;
    reportUploadProgress({ completed: 0, total });
    try {
      const photos = await bulkUploadPhotos(files, photoUploader, completed => reportUploadProgress({ completed, total }));
      if (!photos.length) return photos;
      await scanDataMutators.updateScanPart(scanPartIds, { photos: getUniqueScanPhotos(scanPart.photos, photos) });
      trackEvent(ANALYTICS_EVENTS.scanPhotosUploaded, {
        scanId,
        photoCount: photos.length,
        scope: scanPartIds.lineItemId ? 'line_item' : scanPartIds.optionId ? 'option' : scanPartIds.taskId ? 'task' : scanPartIds.groupId ? 'group' : 'scope',
      });
      return photos;
    } finally {
      reportUploadProgress(null);
    }
  };
};
